import { type AnyElement, type AnyElementData } from '../all-element-types';
import {
  type ElementData,
  type Element,
  type ElementId,
  elementType,
  type ElementType,
} from '../element';

import type { PathElementData } from './path';

/**
 * A number between 0 and 1 relative to a distance.
 * @description Used to position anchors relative to a width (left) or height (top).
 */
export type Proportion = number;

/**
 * Where a path is anchored to an element.
 */
export type ElementAnchor = {
  /**
   * A value between 0 and 1 representing the percentange distance from the left of the element's
   * bounding box where the anchor is placed.
   * @description The bounding box is the rectangular bounding box.
   */
  left: Proportion;
  /**
   * A value between 0 and 1 representing the percentange distance from the top of the element's
   * bounding box where the anchor is placed.
   * @description The bounding box is the rectangular bounding box.
   */
  top: Proportion;
};

export interface ConnectableElement {
  /**
   * The element which this path starts from.
   * @description This element will be another element on the Canvas board.
   * @remark We expect this property to change alongside {@link sourceAnchor} if modifying the connector
   * to start from a different element.
   * @remark We will avoid interleaved writes between the positional data for a {@link PathElement} and
   * conceptual data representing which element it starts from.
   */
  sourceElement?: ElementId;
  /**
   * The element which this path ends at.
   * @description This element will be another element on the Canvas board.
   * @remark We expect this property to change alongside {@link targetAnchor} if modifying the connector
   * to end at a different element.
   * @remark We will avoid interleaved writes between the positional data for a {@link PathElement} and
   * conceptual data representing which element it ends at.
   */
  targetElement?: ElementId;
}

/**
 * Determines if an element is connectable. That is, it has a `sourceElement` and
 * `targetElement` property that refer to other elements on the board.
 *
 * @param element The element to check.
 * @returns `true` if the element is connectable, `false` otherwise.
 */
export function isConnectable(
  element: Partial<AnyElement> | ConnectableElement | undefined
): element is ConnectableElement {
  if (!element) {
    return false;
  }

  if (!('sourceElement' in element) && !('targetElement' in element)) {
    // If an element has neither sourceElement nor targetElement, it is not connectable
    return false;
  }

  if (
    'sourceElement' in element &&
    element.sourceElement &&
    typeof element.sourceElement !== 'string'
  ) {
    // If sourceElement is defined, it must be a string
    return false;
  }

  if (
    'targetElement' in element &&
    element.targetElement &&
    typeof element.targetElement !== 'string'
  ) {
    // If targetElement is defined, it must be a string
    return false;
  }

  return true;
}

export interface ConnectorElementPasteData extends ConnectorElementData {
  /**
   * The index of the connector's source element in the array of copied elements.
   */
  sourceIndex?: number;
  /**
   * The index of the connector's target element in the array of copied elements.
   */
  targetIndex?: number;
}

export interface ConnectorElementData
  extends Omit<PathElementData, 'type'>,
    ConnectableElement,
    ElementData<ElementType> {
  /**
   * The anchor on the {@link sourceElement} from where a path starts.
   * @description Stored as an enum on the Document to optimize for document size.
   */
  sourceAnchor?: ElementAnchor;
  /**
   * The anchor on the {@link targetElement} at which a path ends.
   * @description Stored as an enum on the Document to optimize for document size.
   */
  targetAnchor?: ElementAnchor;
}

export interface ConnectorElement extends ConnectorElementData, Element<ElementType> {}

export function isConnector(
  element: AnyElement | undefined
): element is ConnectorElement {
  return element?.type === connectorElementType;
}

export function isConnectorData(
  element: AnyElementData
): element is ConnectorElementData {
  return element?.type === connectorElementType;
}
export const connectorElementType = elementType('connector');
